import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Leaf } from "lucide-react";
import { NAV_LINKS } from "@/constants/site";

const Logo = ({ light }) => (
    <Link
        to="/"
        data-testid="header-logo"
        className="flex items-center gap-2.5 group"
    >
        <span
            className={`flex h-9 w-9 items-center justify-center rounded-full transition-colors ${
                light ? "bg-white/15 text-white" : "bg-rz-green text-white"
            }`}
        >
            <Leaf size={18} strokeWidth={2.2} />
        </span>
        <span className="leading-none">
            <span
                className={`font-display block text-[15px] font-extrabold tracking-tight ${
                    light ? "text-white" : "text-rz-ink"
                }`}
            >
                ROZIER CONCEPT
            </span>
            <span
                className={`block text-[10px] tracking-[0.22em] uppercase ${
                    light ? "text-white/70" : "text-rz-ink-soft"
                }`}
            >
                Agricole &amp; Haras
            </span>
        </span>
    </Link>
);

export const Header = () => {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 30);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        setOpen(false);
    }, [location.pathname]);

    const onHome = location.pathname === "/";
    const transparent = onHome && !scrolled;

    return (
        <header
            data-testid="site-header"
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
                transparent
                    ? "bg-transparent py-5"
                    : "bg-white/80 backdrop-blur-xl backdrop-saturate-150 border-b border-black/5 py-3 shadow-[0_4px_30px_rgba(10,26,18,0.04)]"
            }`}
        >
            <div className="mx-auto max-w-[1400px] px-6 md:px-12 flex items-center justify-between">
                <Logo light={transparent} />

                <nav className="hidden lg:flex items-center gap-1">
                    {NAV_LINKS.slice(0, 4).map((link) => {
                        const active = location.pathname === link.to;
                        return (
                            <Link
                                key={link.to}
                                to={link.to}
                                data-testid={`nav-link-${link.to.replace("/", "") || "home"}`}
                                className={`relative px-3.5 py-2 text-sm font-medium transition-colors duration-300 ${
                                    transparent
                                        ? "text-white/85 hover:text-white"
                                        : active
                                        ? "text-rz-green"
                                        : "text-rz-ink-soft hover:text-rz-green"
                                }`}
                            >
                                {link.label}
                                {active && !transparent && (
                                    <span className="absolute left-3.5 right-3.5 -bottom-0.5 h-0.5 rounded-full bg-rz-sage" />
                                )}
                            </Link>
                        );
                    })}
                </nav>

                <div className="flex items-center gap-3">
                    <Link
                        to="/contact"
                        data-testid="header-contact-cta"
                        className={`hidden sm:inline-flex items-center rounded-full px-5 py-2.5 text-sm font-semibold transition-all duration-300 hover:-translate-y-0.5 ${
                            transparent
                                ? "bg-white text-rz-green hover:shadow-lg hover:shadow-black/20"
                                : "bg-rz-green text-white hover:bg-rz-deep hover:shadow-lg hover:shadow-rz-green/30"
                        }`}
                    >
                        Contact
                    </Link>
                    <button
                        data-testid="mobile-menu-toggle"
                        onClick={() => setOpen((v) => !v)}
                        aria-label="Menu"
                        className={`lg:hidden inline-flex h-10 w-10 items-center justify-center rounded-full ${
                            transparent ? "text-white" : "text-rz-ink"
                        }`}
                    >
                        {open ? <X size={22} /> : <Menu size={22} />}
                    </button>
                </div>
            </div>

            {open && (
                <div
                    data-testid="mobile-menu"
                    className="lg:hidden bg-white border-t border-black/5 px-6 py-4"
                >
                    {NAV_LINKS.map((link) => (
                        <Link
                            key={link.to}
                            to={link.to}
                            data-testid={`mobile-nav-link-${link.to.replace("/", "") || "home"}`}
                            className="block py-3 text-base font-medium text-rz-ink border-b border-black/5 last:border-0"
                        >
                            {link.label}
                        </Link>
                    ))}
                </div>
            )}
        </header>
    );
};
